import React, { useEffect, useState } from 'react';
import type { Page } from '../types';
import { collection, doc, getDoc, limit, onSnapshot, orderBy, query, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions, getFirestoreDB } from '../lib/firebase';
import BackToPosButton from '../components/common/BackToPosButton';
import type { CateringAlertSettings } from '../types';
import CateringAlertsFields, { DEFAULT_CATERING_ALERTS, invalidEmails } from '../components/settings/CateringAlertsFields';

type CateringStatus = 'new' | 'quoted' | 'confirmed' | 'declined';

interface CateringInquiryRow {
  id: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string | null;
  preferredContact?: string;
  occasion?: string;
  occasionDetail?: string;
  guestCount?: number;
  childrenCount?: number | null;
  venue?: string;
  venueAddress?: string;
  serviceLevel?: string | null;
  extras?: string[];
  referral?: string | null;
  eventDate?: string;
  serviceTime?: string;
  datesFlexible?: boolean;
  flexibilityNotes?: string;
  budgetPerPersonCents?: number | null;
  /** Legacy requests (before the quote form) stored a band like "20-50". */
  guestBand?: string;
  serviceType?: string | null;
  packageName?: string | null;
  pricePerGuestCents?: number | null;
  dietary?: string[];
  notes?: string;
  status?: CateringStatus;
  staffNote?: string | null;
  /** Stamped by the onCateringInquiryCreated function. */
  alert?: { status: string; sentTo?: string[]; error?: string };
  createdAt?: number;
}

interface CateringPackageDraft {
  id: string;
  name: string;
  description: string;
  pricePerGuest: number;
  isAvailable: boolean;
}

interface CateringPageProps {
  onNavigate?: (page: Page) => void;
}

const STATUS_STYLES: Record<CateringStatus, string> = {
  new: 'bg-pink-100 text-pink-800',
  quoted: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-green-100 text-green-800',
  declined: 'bg-gray-200 text-gray-700',
};

const SERVICE_TYPE_LABELS: Record<string, string> = {
  family_style: 'Family-style sharing',
  passed_canapes: 'Canapés / finger food',
  set_menu: 'Set menu',
  buffet: 'Buffet',
};

const OCCASION_LABELS: Record<string, string> = {
  birthday: 'Birthday',
  wedding: 'Wedding / engagement',
  private_gathering: 'Private get-together',
  catch_up: 'Catch-up',
  corporate: 'Corporate / work',
  other: 'Other occasion',
};

const VENUE_LABELS: Record<string, string> = {
  kings_park: 'At Kings Park',
  own_venue: "Customer's venue",
  pickup: 'Pickup',
};

const CONTACT_LABELS: Record<string, string> = { call: 'a call', text: 'a text', email: 'email' };

const ALERT_LABELS: Record<string, string> = {
  sent: 'Email alert sent',
  failed: 'Email alert failed',
  disabled: 'Email alerts off',
  no_recipients: 'No alert email set',
};

const SERVICE_LEVEL_LABELS: Record<string, string> = {
  drop_off: 'Drop-off only',
  delivery_setup: 'Delivery & setup',
  full_service: 'Setup + staff to serve',
};

const EXTRA_LABELS: Record<string, string> = { cake: 'Celebration cake', decorations: 'Decorations', drinks: 'Drinks package' };

const REFERRAL_LABELS: Record<string, string> = {
  google: 'Google',
  social_media: 'Social media',
  friend_family: 'Friend / family',
  returning: 'Returning customer',
  other: 'Other',
};

const money = (cents?: number | null) => (cents == null ? '' : `$${(cents / 100).toFixed(2)}`);

/**
 * Catering & private events — inquiries submitted from the customer app (mirrored into
 * `catering_inquiries`) plus the package list the app offers (`public_config/catering`).
 * Status changes go through `updateCateringInquiryStatus`, which also notifies the customer.
 */
const CateringPage: React.FC<CateringPageProps> = ({ onNavigate }) => {
  const [tab, setTab] = useState<'inquiries' | 'packages'>('inquiries');
  const [inquiries, setInquiries] = useState<CateringInquiryRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, { status: CateringStatus; staffNote: string }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [rowMessage, setRowMessage] = useState<Record<string, string>>({});

  const [enabled, setEnabled] = useState(true);
  const [packages, setPackages] = useState<CateringPackageDraft[]>([]);
  const [packagesMessage, setPackagesMessage] = useState<string | null>(null);

  const [alerts, setAlerts] = useState<CateringAlertSettings>(DEFAULT_CATERING_ALERTS);
  const [alertsMessage, setAlertsMessage] = useState<string | null>(null);

  useEffect(() => {
    getDoc(doc(getFirestoreDB(), 'integration_settings', 'main'))
      .then((snap) => {
        const saved = snap.data()?.cateringAlerts as CateringAlertSettings | undefined;
        if (saved) setAlerts({ enabled: saved.enabled !== false, emails: Array.isArray(saved.emails) ? saved.emails : [] });
      })
      .catch(() => setAlertsMessage('Could not load alert settings.'));
  }, []);

  const saveAlerts = async () => {
    setAlertsMessage(null);
    const bad = invalidEmails(alerts.emails);
    if (alerts.enabled && bad.length > 0) {
      setAlertsMessage(`Fix these addresses first: ${bad.join(', ')}`);
      return;
    }
    try {
      await setDoc(doc(getFirestoreDB(), 'integration_settings', 'main'), { cateringAlerts: alerts }, { merge: true });
      setAlertsMessage('Saved. New requests will be emailed to these addresses.');
    } catch (err) {
      setAlertsMessage(err instanceof Error ? `${err.message} (only admins can change settings)` : 'Save failed.');
    }
  };

  useEffect(() => {
    const q = query(collection(getFirestoreDB(), 'catering_inquiries'), orderBy('createdAt', 'desc'), limit(100));
    return onSnapshot(
      q,
      (snap) => {
        setLoadError(null);
        setInquiries(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CateringInquiryRow, 'id'>) })));
      },
      (err) => setLoadError(err.message || 'Could not load catering inquiries.'),
    );
  }, []);

  useEffect(() => {
    getDoc(doc(getFirestoreDB(), 'public_config', 'catering'))
      .then((snap) => {
        const data = snap.data() ?? {};
        setEnabled(snap.exists() ? data.enabled !== false : true);
        const raw: unknown[] = Array.isArray(data.packages) ? data.packages : [];
        setPackages(
          raw
            .filter((p): p is Record<string, unknown> => typeof p === 'object' && p !== null)
            .map((p) => ({
              id: String(p.id ?? ''),
              name: String(p.name ?? ''),
              description: String(p.description ?? ''),
              pricePerGuest: Number(p.pricePerGuest) || 0,
              isAvailable: p.isAvailable !== false,
            })),
        );
      })
      .catch((err) => setPackagesMessage(err instanceof Error ? err.message : 'Could not load packages.'));
  }, []);

  const editFor = (row: CateringInquiryRow) =>
    edits[row.id] ?? { status: row.status ?? 'new', staffNote: row.staffNote ?? '' };

  const saveStatus = async (row: CateringInquiryRow) => {
    const edit = editFor(row);
    setSavingId(row.id);
    setRowMessage((m) => ({ ...m, [row.id]: '' }));
    try {
      await httpsCallable(getFirebaseFunctions(), 'updateCateringInquiryStatus')({
        inquiryId: row.id,
        status: edit.status,
        staffNote: edit.staffNote,
      });
      setRowMessage((m) => ({ ...m, [row.id]: 'Saved — customer notified.' }));
      setEdits((e) => {
        const next = { ...e };
        delete next[row.id];
        return next;
      });
    } catch (err) {
      setRowMessage((m) => ({ ...m, [row.id]: err instanceof Error ? err.message : 'Save failed.' }));
    } finally {
      setSavingId(null);
    }
  };

  const savePackages = async () => {
    setPackagesMessage(null);
    const cleaned = packages
      .map((p) => ({ ...p, name: p.name.trim(), description: p.description.trim() }))
      .filter((p) => p.name);
    try {
      await setDoc(
        doc(getFirestoreDB(), 'public_config', 'catering'),
        {
          enabled,
          packages: cleaned.map((p) => ({
            id: p.id || p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
            name: p.name,
            description: p.description,
            pricePerGuest: Math.max(0, Number(p.pricePerGuest) || 0),
            isAvailable: p.isAvailable,
          })),
          updatedAt: Date.now(),
        },
        { merge: true },
      );
      setPackagesMessage('Saved. The app picks up changes the next time the Catering tab opens.');
    } catch (err) {
      setPackagesMessage(
        err instanceof Error ? `${err.message} (only admins can change catering packages)` : 'Save failed.',
      );
    }
  };

  const updatePackage = (index: number, patch: Partial<CateringPackageDraft>) =>
    setPackages((list) => list.map((p, i) => (i === index ? { ...p, ...patch } : p)));

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Catering &amp; private events</h1>
          <p className="text-sm text-gray-500">Inquiries from the customer app. No payment is taken — follow up with a quote.</p>
        </div>
        <div className="flex items-center gap-2">
          {(['inquiries', 'packages'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`text-sm px-3 py-2 rounded-lg border ${tab === t ? 'bg-pink-500 text-white border-pink-500' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              {t === 'inquiries' ? 'Requests' : 'Settings'}
            </button>
          ))}
          {onNavigate && (
            <button type="button" className="text-sm text-pink-600 ml-2" onClick={() => onNavigate('loyalty')}>
              ← Loyalty &amp; offers
            </button>
          )}
          <BackToPosButton />
        </div>
      </header>

      <main className="flex-1 p-6 overflow-y-auto space-y-4">
        {tab === 'inquiries' && (
          <>
            {loadError && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">{loadError}</p>}
            {inquiries.length === 0 && !loadError && <p className="text-sm text-gray-500">No catering inquiries yet.</p>}
            {inquiries.map((row) => {
              const edit = editFor(row);
              const status = row.status ?? 'new';
              return (
                <div key={row.id} className="bg-white border rounded-lg p-4 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {OCCASION_LABELS[row.occasion ?? ''] ?? 'Catering'}
                        {row.occasionDetail ? ` — ${row.occasionDetail}` : ''}
                      </p>
                      <p className="text-sm text-gray-700">
                        {row.contactName || 'Customer'}
                        {row.contactPhone ? ` · ${row.contactPhone}` : ''}
                        {row.contactEmail ? ` · ${row.contactEmail}` : ''}
                        {row.preferredContact ? ` · prefers ${CONTACT_LABELS[row.preferredContact] ?? row.preferredContact}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`text-xs font-semibold uppercase rounded-full px-2 py-1 ${STATUS_STYLES[status]}`}>{status}</span>
                      {row.alert && (
                        <span
                          className={`text-xs ${row.alert.status === 'sent' ? 'text-green-700' : row.alert.status === 'failed' ? 'text-red-600' : 'text-gray-500'}`}
                          title={row.alert.error || ''}
                        >
                          {ALERT_LABELS[row.alert.status] ?? row.alert.status}
                          {row.alert.status === 'sent' && row.alert.sentTo?.length ? ` to ${row.alert.sentTo.join(', ')}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <dl className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div>
                      <dt className="text-gray-500">Date</dt>
                      <dd className="text-gray-900">
                        {row.eventDate}
                        {row.serviceTime ? ` at ${row.serviceTime}` : ''}
                        {row.datesFlexible ? ' (flexible)' : ''}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Guests</dt>
                      <dd className="text-gray-900">
                        {row.guestCount ?? row.guestBand ?? '—'}
                        {row.childrenCount ? ` (${row.childrenCount} children)` : ''}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Venue</dt>
                      <dd className="text-gray-900">
                        {VENUE_LABELS[row.venue ?? ''] ?? '—'}
                        {row.venueAddress ? `: ${row.venueAddress}` : ''}
                        {row.serviceLevel ? ` · ${SERVICE_LEVEL_LABELS[row.serviceLevel] ?? row.serviceLevel}` : ''}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Extras</dt>
                      <dd className="text-gray-900">
                        {row.extras?.length ? row.extras.map((e) => EXTRA_LABELS[e] ?? e).join(', ') : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Heard via</dt>
                      <dd className="text-gray-900">{row.referral ? REFERRAL_LABELS[row.referral] ?? row.referral : '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Service</dt>
                      <dd className="text-gray-900">{SERVICE_TYPE_LABELS[row.serviceType ?? ''] ?? 'No preference'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Package</dt>
                      <dd className="text-gray-900">
                        {row.packageName ? `${row.packageName} (${money(row.pricePerGuestCents)}/guest)` : 'Custom quote'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Budget / person</dt>
                      <dd className="text-gray-900">{row.budgetPerPersonCents ? money(row.budgetPerPersonCents) : '—'}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-gray-500">Dietary</dt>
                      <dd className="text-gray-900">{row.dietary?.length ? row.dietary.join(', ') : '—'}</dd>
                    </div>
                  </dl>
                  {row.datesFlexible && row.flexibilityNotes && (
                    <p className="text-sm text-gray-700"><span className="text-gray-500">Other dates: </span>{row.flexibilityNotes}</p>
                  )}
                  {row.notes && <p className="text-sm text-gray-800 bg-gray-50 rounded-md p-3 whitespace-pre-wrap">{row.notes}</p>}
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      className="border rounded-md px-3 py-2 text-sm"
                      value={edit.status}
                      onChange={(e) => setEdits((m) => ({ ...m, [row.id]: { ...edit, status: e.target.value as CateringStatus } }))}
                    >
                      <option value="new">New</option>
                      <option value="quoted">Quoted</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="declined">Declined</option>
                    </select>
                    <input
                      className="flex-1 min-w-[12rem] border rounded-md px-3 py-2 text-sm"
                      placeholder="Note to customer (quote, questions)…"
                      value={edit.staffNote}
                      onChange={(e) => setEdits((m) => ({ ...m, [row.id]: { ...edit, staffNote: e.target.value } }))}
                    />
                    <button
                      type="button"
                      disabled={savingId === row.id}
                      onClick={() => void saveStatus(row)}
                      className="px-3 py-2 bg-pink-500 text-white rounded-md text-sm disabled:opacity-50"
                    >
                      {savingId === row.id ? 'Saving…' : 'Save & notify'}
                    </button>
                  </div>
                  {rowMessage[row.id] && <p className="text-sm text-gray-600">{rowMessage[row.id]}</p>}
                </div>
              );
            })}
          </>
        )}

        {tab === 'packages' && (
          <div className="bg-white border rounded-lg p-4 space-y-4 max-w-3xl">
            <h2 className="text-lg font-semibold text-gray-900">Email alerts</h2>
            <CateringAlertsFields value={alerts} onChange={setAlerts} />
            <div className="flex items-center gap-3">
              <button type="button" className="px-3 py-2 bg-pink-500 text-white rounded-md text-sm" onClick={() => void saveAlerts()}>
                Save alerts
              </button>
              {alertsMessage && <p className="text-sm text-gray-600">{alertsMessage}</p>}
            </div>
          </div>
        )}

        {tab === 'packages' && (
          <div className="bg-white border rounded-lg p-4 space-y-4 max-w-3xl">
            <h2 className="text-lg font-semibold text-gray-900">Packages</h2>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4" />
              Accept catering inquiries in the customer app
            </label>
            <p className="text-xs text-gray-500">Prices are per guest, GST inclusive. With no packages the app offers “custom quote” only.</p>
            {packages.map((p, i) => (
              <div key={i} className="border rounded-md p-3 space-y-2">
                <div className="flex gap-2">
                  <input
                    className="flex-1 border rounded-md px-3 py-2 text-sm"
                    placeholder="Package name"
                    value={p.name}
                    onChange={(e) => updatePackage(i, { name: e.target.value })}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="w-32 border rounded-md px-3 py-2 text-sm"
                    placeholder="$ / guest"
                    value={p.pricePerGuest || ''}
                    onChange={(e) => updatePackage(i, { pricePerGuest: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <textarea
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  rows={2}
                  placeholder="What's included"
                  value={p.description}
                  onChange={(e) => updatePackage(i, { description: e.target.value })}
                />
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={p.isAvailable} onChange={(e) => updatePackage(i, { isAvailable: e.target.checked })} />
                    Available
                  </label>
                  <button type="button" className="text-sm text-red-600" onClick={() => setPackages((l) => l.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                onClick={() => setPackages((l) => [...l, { id: '', name: '', description: '', pricePerGuest: 0, isAvailable: true }])}
              >
                + Add package
              </button>
              <button type="button" className="px-3 py-2 bg-pink-500 text-white rounded-md text-sm" onClick={() => void savePackages()}>
                Save packages
              </button>
            </div>
            {packagesMessage && <p className="text-sm text-gray-600">{packagesMessage}</p>}
          </div>
        )}
      </main>
    </div>
  );
};

export default CateringPage;
